import { Component, Inject, ErrorHandler, ViewChild, HostListener, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal } from '@angular/core'
import { Validators } from '@angular/forms'
import { disabled, FieldTree, form, required } from '@angular/forms/signals'

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'

import {
  Kirjanpitotili,
  OstoAlvt,
  MyyntiAlvt,
  AlvMaaritys,
  LokalisoidutAlvtyypit,
  LokalisoituKirjanpitotilinAlvTyyppi,
  KirjanpitotilinAlvTyyppi,
  MonikielinenTeksti,
} from 'app/_jaettu-lemonator/model/kirjanpito'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'

import { firstValueFrom, Observable, Subject, takeUntil } from 'rxjs'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'

export interface KirjanpitotiliMuokkaaDialogData {
  tili: Kirjanpitotili
  lisaa: boolean
}

export interface TiliLomake {
  vanhempi: string | null
  numero: string | number | null
  nimiFi: string
  nimiEn: string
  alv: AlvMaaritys | null
  alvtyyppi: KirjanpitotilinAlvTyyppi
  aktiivinen: boolean
  reskontraActive: boolean
}

@Component({
  templateUrl: './kirjanpitotili-muokkaa.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class KirjanpitotiliMuokkaaDialog implements OnInit, OnDestroy {
  @ViewChild('nimiFiInput', { read: MatInput, static: true }) nimiFiInput: MatInput
  @ViewChild('numeroInput', { read: MatInput, static: true }) numeroInput: MatInput

  commonError: string = ''
  lomake: FieldTree<TiliLomake>
  name = '' + Math.random()
  lisaa = false
  readonly alvt = signal<AlvMaaritys[]>([])
  lokalisoidutAlvtyypit: LokalisoituKirjanpitotilinAlvTyyppi[] = LokalisoidutAlvtyypit.kaikki
  tilitObservable: Observable<Kirjanpitotili[]>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _alvPoissa = signal(false)

  vertaaAlvMaarityksia: (a: AlvMaaritys, b: AlvMaaritys) => boolean = (a: AlvMaaritys, b: AlvMaaritys): boolean => {
    return a && b && a.tunniste === b.tunniste
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: KirjanpitotiliMuokkaaDialogData,
    private _dialogRef: MatDialogRef<KirjanpitotiliMuokkaaDialog>,
    private _errorHandler: ErrorHandler,
    private _tilikarttaService: TilikarttaService,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _dialog: MatDialog,
  ) {
    this.lisaa = this._data.lisaa
    this.tilitObservable = this._tilikarttaService.paatilikartanTilitObservable
  }

  private asetaAlvt(tyyppi: KirjanpitotilinAlvTyyppi) {
    if (tyyppi === KirjanpitotilinAlvTyyppi.MYYNTI) {
      this.alvt.set([].concat(MyyntiAlvt.kaikki))
    } else if (tyyppi === KirjanpitotilinAlvTyyppi.OSTO) {
      this.alvt.set([].concat(OstoAlvt.kaikki))
    } else if (tyyppi === KirjanpitotilinAlvTyyppi.ETAMYYNTI_EI_REKISTEROITYNYT) {
      this.alvt.set([].concat(MyyntiAlvt.suomi))
    } else {
      this.alvt.set([])
    }
  }

  ngOnInit() {
    const kaytettavaTyyppi = this._data.tili.alvTyyppi || KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA
    const onEiAlvKasittelya = kaytettavaTyyppi === KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA || kaytettavaTyyppi === KirjanpitotilinAlvTyyppi.ETAMYYNTI

    this.asetaAlvt(kaytettavaTyyppi)

    const injector = this._injector
    this._alvPoissa.set(onEiAlvKasittelya)
    this.lomake = form(
      signal<TiliLomake>({
        vanhempi: this._data.tili.vanhempi ?? null,
        numero: this._data.tili.numero ?? null,
        nimiFi: this._data.tili.localisedName?.fi ?? this._data.tili.nimi ?? '',
        nimiEn: this._data.tili.localisedName?.en ?? '',
        alv: onEiAlvKasittelya ? null : (this._data.tili.oletusAlvKasittely ?? null),
        alvtyyppi: kaytettavaTyyppi,
        aktiivinen: this._data.tili.aktiivinen ?? false,
        reskontraActive: this._data.tili.reskontraActive ?? false,
      }),
      polku => {
        required(polku.numero)
        validoiReaktiivisesti(polku.numero, Validators.maxLength(4))
        validoiReaktiivisesti(polku.numero, Validators.minLength(1))
        if (!this._data.lisaa) {
          disabled(polku.numero)
        }
        required(polku.nimiFi)
        required(polku.nimiEn)
        required(polku.alv)
        disabled(polku.alv, () => this._alvPoissa())
        required(polku.alvtyyppi)
      },
      { injector },
    )

    arvonMuutokset(this.lomake.alvtyyppi, { injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(value => {
        asetaArvo(this.lomake.alv, null)
        this.asetaAlvt(value)
        if (value && value !== KirjanpitotilinAlvTyyppi.EI_ALV_KASITTELYA && value !== KirjanpitotilinAlvTyyppi.ETAMYYNTI) {
          console.log('enable')
          this._alvPoissa.set(false)
        } else {
          console.log('disable')
          this._alvPoissa.set(true)
        }
      })

    afterNextRender(
      () => {
        if (this.lisaa) {
          this.numeroInput.focus()
        } else {
          this.nimiFiInput.focus()
        }
      },
      { injector },
    )

    arvonMuutokset(this.lomake.reskontraActive, { injector })
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(async isActive => {
        if (
          this._data.tili.reskontraActive && // Was active before
          !isActive // But was just marked as NOT active
        ) {
          const dialogData: AreYouSureDialogData = {
            text: 'Jos reskontra laitetaan pois päältä, kaikki reskontramerkinnät poistetaan.',
            rightAction: 'Kyllä',
          }
          const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: dialogData }).afterClosed())

          if (!isSure) {
            asetaArvo(this.lomake.reskontraActive, true)
          }
        }
      })
  }

  naytaVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return tila.errors().some(virhe => virhe.kind === laji) && (tila.dirty() || tila.touched())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  @HostListener('document:keydown.esc')
  peruuta() {
    this._dialogRef.close()
  }

  @HostListener('document:keydown.enter')
  async tallenna() {
    if (this.lomake().invalid() || !this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this._ladataanService.aloitaLataaminen()

    try {
      const localisedName: MonikielinenTeksti = {
        fi: null,
        en: null,
      }

      const arvot = this.lomake().value()
      if (arvot.nimiFi) {
        localisedName.fi = arvot.nimiFi
      }
      if (arvot.nimiEn) {
        localisedName.en = arvot.nimiEn
      }

      const tili: Kirjanpitotili = {
        vanhempi: arvot.vanhempi ? arvot.vanhempi + '' : null,
        aktiivinen: !!arvot.aktiivinen,
        numero: arvot.numero ? arvot.numero + '' : null,
        nimi: arvot.nimiFi,
        oletusAlvKasittely: arvot.alv,
        alvTyyppi: arvot.alvtyyppi,
        localisedName: localisedName,
      }

      if (arvot.reskontraActive) {
        tili.reskontraActive = true
      }
      const kirjanpitajanTiedot = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
      await this._tilikarttaService.tallennaPaatilikartanTili(tili, kirjanpitajanTiedot.uid)
    } catch (error) {
      this.commonError = 'Tallentamisen aikana tapahtui virhe. Ole hyvä ja ilmoita tästä ylläpitäjälle.'
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._dialogRef.close()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
